import React, { useState, useRef, useCallback, useEffect } from 'react';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
	Box,
	TextField,
	IconButton,
	Typography,
	Divider,
	Switch,
	FormControl,
	Select,
	InputLabel,
	MenuItem,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import useDeepCompareEffect from 'use-deep-compare-effect';

import {
	IOptionData,
	IQuestionBlockProps,
	IQuestionData,
	QuestionType,
} from 'src/features/questionnaire_template/domain/types';

import { OptionList } from './OptionList';

const requiredSwitchSx = {
	'& .MuiSwitch-switchBase.Mui-checked': {
		color: '#E53326',
	},
	'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
		backgroundColor: '#E53326',
	},
};

export const QuestionBlock: React.FC<IQuestionBlockProps> = ({
	id,
	onDelete,
	onChange,
	defaultData,
	validation,
	showRequiredForCompletion = true,
}) => {
	const [questionData, setQuestionData] = useState<IQuestionData>(
		defaultData || {
			text: '',
			serial_number: '',
			type: '',
			is_required: false,
			is_required_for_completion: false,
			options: [],
		},
	);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const enableOnChangeUseEffectRef = useRef<boolean>(false);
	const enableDefaultDataChangeUseEffectRef = useRef<boolean>(false);

	const handleAddOption = () => {
		setQuestionData((prev) => ({
			...prev,
			options: [
				...(prev.options || []),
				{ value: '', allow_text: false, label: '' },
			],
		}));
	};

	const handleOptionListChange = useCallback(
		(updatedOptions: IOptionData[]) => {
			setQuestionData((prev) => ({
				...prev,
				is_updated: true,
				options: updatedOptions,
			}));
		},
		[],
	);

	const handleQuestionTypeChange = (
		event: SelectChangeEvent<QuestionType>,
		_child?: React.ReactNode,
	) => {
		const newType = event.target.value as QuestionType;

		let updatedOptions: IOptionData[] = [];

		if (newType === 'single_select' || newType === 'multi_select') {
			updatedOptions = questionData.options ?? [];
		} else {
			updatedOptions = (questionData.options ?? [])
				.map((option) => {
					if (option.id) {
						return { ...option, is_deleted: true };
					}
					return null;
				})
				.filter(Boolean) as IOptionData[];
		}

		setQuestionData({
			...questionData,
			type: newType,
			is_updated: true,
			options: updatedOptions,
		});
	};

	useDeepCompareEffect(() => {
		if (!enableOnChangeUseEffectRef.current) {
			enableOnChangeUseEffectRef.current = true;
			return;
		}

		if (debounceRef.current) clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(() => {
			onChange(id, questionData);
		}, 300);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [questionData, id, onChange]);

	useEffect(() => {
		if (!enableDefaultDataChangeUseEffectRef.current) {
			enableDefaultDataChangeUseEffectRef.current = true;
			return;
		}

		if (defaultData) {
			setQuestionData(defaultData);
		}
	}, [defaultData]);

	if (questionData.is_deleted) return null;

	return (
		<Box
			sx={{
				position: 'relative',
				backgroundColor: 'white',
				p: 3,
				borderRadius: '6px',
				border: '2px dashed rgb(71, 71, 73)',
				mt: 2,
			}}
		>
			<Typography
				sx={{
					position: 'absolute',
					top: '-12px',
					left: '12px',
					backgroundColor: 'white',
					px: 1,
					fontSize: '14px',
					color: 'rgb(71, 71, 73)',
				}}
			>
				Question Block
			</Typography>

			<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
				<TextField
					label='Question'
					variant='outlined'
					fullWidth
					value={questionData.text}
					onChange={(e) =>
						setQuestionData({
							...questionData,
							text: e.target.value,
							is_updated: true,
						})
					}
					sx={{
						flex: 2.5,
						'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
							{
								borderColor: '#BE3327',
							},
						'& .MuiInputLabel-root.Mui-focused': {
							color: '#BE3327',
						},
					}}
					error={!!validation?.text}
					helperText={validation?.text}
				/>

				<TextField
					label='Serial Number'
					variant='outlined'
					fullWidth
					value={questionData.serial_number}
					onChange={(e) =>
						setQuestionData({
							...questionData,
							serial_number: e.target.value,
							is_updated: true,
						})
					}
					sx={{
						flex: 1,
						'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
							{
								borderColor: '#BE3327',
							},
						'& .MuiInputLabel-root.Mui-focused': {
							color: '#BE3327',
						},
					}}
					error={!!validation?.serial_number}
					helperText={validation?.serial_number}
				/>

				<FormControl
					fullWidth
					sx={{
						flex: 1,
						minWidth: 180,
						'& .MuiInputLabel-root.Mui-focused': {
							color: '#BE3327',
						},
					}}
					error={!!validation?.type}
				>
					<InputLabel>Type</InputLabel>
					<Select
						label='Type'
						value={questionData.type}
						onChange={handleQuestionTypeChange}
						sx={{
							textAlign: 'left',
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: '#BE3327',
							},
						}}
					>
						<MenuItem value='text'>Text</MenuItem>
						<MenuItem value='text_area'>Textarea</MenuItem>
						<MenuItem value='integer'>Integer</MenuItem>
						<MenuItem value='decimal'>Decimal</MenuItem>
						<MenuItem value='single_select'>Single Select</MenuItem>
						<MenuItem value='multi_select'>Multi Select</MenuItem>
						<MenuItem value='date'>Date</MenuItem>
						<MenuItem value='datetime'>DateTime</MenuItem>
						<MenuItem value='image'>Image</MenuItem>
						<MenuItem value='file'>File</MenuItem>
					</Select>

					{validation?.type && (
						<Typography
							variant='caption'
							color='error'
							sx={{ mt: 0.5, ml: 2 }}
						>
							{validation.type}
						</Typography>
					)}
				</FormControl>
			</Box>

			{/* Options List */}
			{(questionData.type === 'single_select' ||
				questionData.type === 'multi_select') && (
				<Box sx={{ mt: 2 }}>
					<OptionList
						options={questionData.options || []}
						onChange={handleOptionListChange}
						validation={validation?.options}
					/>

					<Typography
						sx={{
							display: 'flex',
							alignItems: 'center',
							color: '#666',
							fontWeight: 500,
							fontSize: '14px',
							mb: 1,
							cursor: 'pointer',
							'&:hover': {
								color: '#BE3327',
							},
						}}
						onClick={handleAddOption}
					>
						<RadioButtonUncheckedIcon
							sx={{ fontSize: 18, mr: 2 }}
						/>
						Add Option
					</Typography>

					{validation?.optionError && (
						<Typography
							color='error'
							sx={{
								mt: 1,
								textAlign: 'left',
								fontWeight: 500,
								fontSize: '12px',
							}}
						>
							{validation.optionError}
						</Typography>
					)}
				</Box>
			)}

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					mt: 2,
					gap: 1,
				}}
			>
				<IconButton onClick={() => onDelete(id)}>
					<img
						src='/delete.svg'
						alt='delete'
						width={16}
						height={18}
					/>
				</IconButton>

				<Divider orientation='vertical' flexItem />

				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Typography fontSize='16px' lineHeight='24px'>
						Required
					</Typography>
					<Switch
						checked={questionData.is_required}
						onChange={(e) =>
							setQuestionData({
								...questionData,
								is_required: e.target.checked,
								is_updated: true,
							})
						}
						sx={requiredSwitchSx}
					/>
				</Box>
				{showRequiredForCompletion && (
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Typography fontSize='16px' lineHeight='24px'>
							Required for completion
						</Typography>
						<Switch
							checked={questionData.is_required_for_completion}
							onChange={(e) =>
								setQuestionData({
									...questionData,
									is_required_for_completion:
										e.target.checked,
									is_updated: true,
								})
							}
							sx={requiredSwitchSx}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};
